/**
 * Skill Editor
 * Edits a single skill configuration with AI-assisted generation
 */

import React, { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Trash2, ChevronDown, ChevronUp, AlertCircle, Sparkles, Loader2, Layers } from 'lucide-react';
import { useChatModelsQuery } from '@/lib/queries/useModelsQuery';
import { skillGenerationApi } from '@/lib/api/skillGeneration';
import { useToolComboTemplates } from '@/features/system-settings/hooks/useSystemConfig';
import type { SkillConfig } from '@/types';

// Validate skill_id format: lowercase letters, numbers, underscores, must start with a letter
const SKILL_ID_PATTERN = /^[a-z][a-z0-9_]*$/;
const normalizeSkillId = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, '');
};

interface SkillEditorProps {
  skill: SkillConfig;
  onChange: (updates: Partial<SkillConfig>) => void;
  onDelete: () => void;
  agentContext?: string;
}

export const SkillEditor: React.FC<SkillEditorProps> = ({
  skill,
  onChange,
  onDelete,
  agentContext,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [skillIdError, setSkillIdError] = useState<string | null>(null);

  // AI Generation state
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [aiExplanation, setAiExplanation] = useState('');
  const [patternExplanation, setPatternExplanation] = useState('');

  // Queries and mutations
  const { data: chatModels = [] } = useChatModelsQuery();
  const { data: toolComboTemplates = [] } = useToolComboTemplates(true, 'cn');
  const generateMutation = useMutation({
    mutationFn: skillGenerationApi.generate,
  });
  const generatePatternsMutation = useMutation({
    mutationFn: skillGenerationApi.generatePatterns,
  });

  const handleSkillIdChange = useCallback((value: string) => {
    const normalized = normalizeSkillId(value);
    if (normalized && !SKILL_ID_PATTERN.test(normalized)) {
      setSkillIdError('Must start with lowercase letter');
    } else {
      setSkillIdError(null);
    }
    onChange({ skill_id: normalized });
  }, [onChange]);

  const handleKeywordsChange = useCallback((value: string) => {
    const keywords = value.split(',').map(k => k.trim()).filter(k => k);
    onChange({ keywords });
  }, [onChange]);

  const handlePatternsChange = useCallback((value: string) => {
    // Don't filter empty lines during editing to allow Enter key to create new lines
    // Empty lines will be filtered when saving
    const patterns = value.split('\n');
    onChange({ patterns });
  }, [onChange]);

  const handleExampleQueriesChange = useCallback((value: string) => {
    // Don't filter empty lines during editing to allow Enter key to create new lines
    // Empty lines will be filtered when saving
    const queries = value.split('\n');
    onChange({ example_queries: queries });
  }, [onChange]);

  const handleDeleteConfirm = () => {
    if (confirm(`Are you sure you want to delete skill "${skill.name}"?`)) {
      onDelete();
    }
  };

  const handleAiGenerate = async () => {
    if (!skill.instructions?.trim()) {
      alert('Please provide instructions for the skill first');
      return;
    }
    if (!selectedModel) {
      alert('Please select a model first');
      return;
    }

    try {
      const result = await generateMutation.mutateAsync({
        instructions: skill.instructions,
        model: selectedModel,
        language: 'zh',
        context: agentContext,
      });

      // Fill form with generated data
      onChange({
        skill_id: skill.skill_id.startsWith('skill_') ? result.skill_id : skill.skill_id,
        name: skill.name === 'New Skill' ? result.name : skill.name,
        description: result.description,
        keywords: result.keywords,
        patterns: result.patterns,
        example_queries: result.example_queries,
        exclusion_group: result.exclusion_group,
        force_response_type: result.force_response_type,
      });

      setAiExplanation(result.explanation);
    } catch (err) {
      console.error('AI generation failed:', err);
      alert('AI generation failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleGeneratePatterns = async () => {
    if (!skill.instructions?.trim()) {
      alert('Please provide instructions for the skill first');
      return;
    }
    if (!selectedModel) {
      alert('Please select a model in the AI Generation panel first');
      return;
    }

    try {
      const result = await generatePatternsMutation.mutateAsync({
        instructions: skill.instructions,
        // Filter empty strings to avoid backend validation issues
        keywords: skill.keywords.filter(k => k.trim()),
        example_queries: skill.example_queries.filter(q => q.trim()),
        model: selectedModel,
        language: 'zh',
      });

      // Append new patterns to existing ones
      const existingPatterns = skill.patterns || [];
      const newPatterns = result.patterns.filter(p => !existingPatterns.includes(p));
      onChange({ patterns: [...existingPatterns, ...newPatterns] });
      setPatternExplanation(result.explanation);
    } catch (err) {
      console.error('Pattern generation failed:', err);
      alert('Pattern generation failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header with actions */}
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Edit Skill</h3>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteConfirm}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Instructions (moved to top for AI generation) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instructions <span className="text-red-500">*</span>
          </label>
          <textarea
            value={skill.instructions}
            onChange={(e) => onChange({ instructions: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            rows={8}
            placeholder="Write detailed instructions for this skill...&#10;&#10;The more detailed your instructions, the better the AI can generate matching rules."
          />
          <p className="mt-1 text-xs text-gray-500">
            Appended to base instructions when skill is matched
          </p>
        </div>

        {/* AI Generation Panel */}
        <div className="border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
          <button
            type="button"
            onClick={() => setShowAiPanel(!showAiPanel)}
            className="flex items-center gap-2 w-full px-4 py-3 text-left"
          >
            <Sparkles className="w-5 h-5 text-purple-500" />
            <span className="flex-1 font-medium text-purple-800">
              AI Generate Matching Rules
            </span>
            <ChevronDown
              className={`w-4 h-4 text-purple-500 transition-transform ${
                showAiPanel ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showAiPanel && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-purple-700">
                Analyze your instructions and automatically generate skill metadata, keywords, patterns, and example queries.
              </p>

              {/* Model Selector */}
              <div>
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  Select Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
                >
                  <option value="">Select a model...</option>
                  {chatModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generate Button */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAiGenerate}
                  disabled={generateMutation.isPending || !skill.instructions?.trim() || !selectedModel}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {generateMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate from Instructions
                    </>
                  )}
                </Button>
              </div>

              {/* AI Explanation */}
              {aiExplanation && (
                <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500">✨</span>
                    <div>
                      <p className="text-xs font-medium text-purple-800 mb-1">
                        AI Analysis
                      </p>
                      <p className="text-sm text-gray-700">{aiExplanation}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Skill ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={skill.skill_id}
              onChange={(e) => handleSkillIdChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-sm font-mono ${
                skillIdError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="annual_report"
            />
            {skillIdError && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {skillIdError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={skill.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Annual Report"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <input
            type="text"
            value={skill.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="Brief description of what this skill does"
          />
          <p className="mt-1 text-xs text-gray-500">
            Always injected into prompt to let agent know its capabilities
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <input
              type="number"
              value={skill.priority}
              onChange={(e) => onChange({ priority: parseInt(e.target.value) || 100 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              min={0}
              max={1000}
            />
            <p className="mt-1 text-xs text-gray-500">Higher wins in conflicts</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exclusion Group
            </label>
            <input
              type="text"
              value={skill.exclusion_group || ''}
              onChange={(e) => onChange({ exclusion_group: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="report_type"
            />
            <p className="mt-1 text-xs text-gray-500">Same group = mutually exclusive</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Force Response Type
            </label>
            <select
              value={skill.force_response_type || ''}
              onChange={(e) => onChange({
                force_response_type: e.target.value as 'quick' | 'deep' | null || null
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Auto (use classifier)</option>
              <option value="quick">Quick Response</option>
              <option value="deep">Deep Response</option>
            </select>
          </div>
        </div>

        {/* Tool Combo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            <Layers className="w-4 h-4 text-blue-600" />
            Tool Combo
          </label>
          <select
            value={skill.tool_combo_id || ''}
            onChange={(e) => onChange({ tool_combo_id: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">None (no tool combo)</option>
            {toolComboTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.tool_count} tools)
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            When this skill matches, the selected tool combo will be injected into the prompt
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`enabled-${skill.skill_id}`}
            checked={skill.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          <label htmlFor={`enabled-${skill.skill_id}`} className="text-sm text-gray-700">
            Enabled
          </label>
        </div>

        {/* Matching Rules Section */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Matching Rules
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keywords (comma-separated)
                </label>
                <input
                  type="text"
                  value={skill.keywords.join(', ')}
                  onChange={(e) => handleKeywordsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="annual, yearly, year report"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Trigger words for rule-based matching
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Regex Patterns (one per line)
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleGeneratePatterns}
                    disabled={generatePatternsMutation.isPending || !skill.instructions?.trim() || !selectedModel}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2"
                  >
                    {generatePatternsMutation.isPending ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        生成中...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI 生成
                      </>
                    )}
                  </Button>
                </div>
                <textarea
                  value={skill.patterns.join('\n')}
                  onChange={(e) => handlePatternsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  rows={3}
                  placeholder="\\d{4}年.{0,5}(运势|报告)"
                />
                {patternExplanation && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700">
                    <span className="font-medium">AI: </span>{patternExplanation}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Regular expressions for pattern matching
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Example Queries (one per line)
                </label>
                <textarea
                  value={skill.example_queries.join('\n')}
                  onChange={(e) => handleExampleQueriesChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows={4}
                  placeholder="帮我看看明年运势&#10;生成一份年度报告&#10;2025年我的运气怎么样"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Sample queries for embedding-based semantic matching
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
